import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { AnalyticsCellRenderer } from '../cellRenderer';
import type { TFiltersChangeHandler, TProps, TRow } from '../types';

import { useAnalyticsTableFilters } from './useAnalyticsTableFilters';

const rightAlignedCellClassName = 'ag-right-aligned-cell';

const autoHeightCellProps = {
    wrapText: true,
    autoHeight: true,
    cellStyle: {
        display: 'flex',
        alignItems: 'center',
    },
};

export const useTableColumns = ({
    workOrderPriceId,
    refWorkOrderPriceId,
    changeFiltersHandler,
}: Omit<TProps, 'priceLevel'> & TFiltersChangeHandler) => {
    const tableFilters = useAnalyticsTableFilters({
        refWorkOrderPriceId,
        workOrderPriceId,
        changeFiltersHandler,
    });

    return useMemo<(ColDef<TRow> | ColGroupDef<TRow>)[]>(
        () => [
            {
                field: 'section',
                headerName: 'Раздел',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'section',
                },
                ...tableFilters.section,
                colSpan: ({ data }) => (data && 'isSection' in data && data?.isSection ? 7 : 1),
            },
            {
                field: 'subsection',
                headerName: 'Заголовок',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'subsection',
                },
                ...tableFilters.subsection,
                colSpan: ({ data }) => (data && 'isSubsection' in data && data?.isSubsection ? 7 : 1),
            },
            {
                field: 'code' as any,
                headerName: 'Шифр',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'code',
                },
                ...tableFilters.code,
                ...autoHeightCellProps,
            },
            {
                field: 'name' as any,
                headerName: 'Наименование работ, затрат',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'name',
                },
                ...tableFilters.name,
                initialWidth: 550,
                ...autoHeightCellProps,
            },
            {
                field: 'unit' as any,
                headerName: 'Ед.изм',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'unit',
                },
                ...tableFilters.unit,
                initialWidth: 120,
            },
            {
                headerName: 'Кол-во (объем)',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'quantity',
                    fractionDigits: 2,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Стоимость единицы',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'unitCost',
                    fractionDigits: 2,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Стоимость строительно-монтажных работ',
                width: 600,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Всего СМР',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'smrTotal',
                            fractionDigits: 0,
                        },
                    },
                    {
                        headerName: 'ОЗП',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'ozp',
                            fractionDigits: 0,
                        },
                    },
                    {
                        headerName: 'ЭММ',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'emm',
                            fractionDigits: 0,
                        },
                    },
                    {
                        headerName: 'В том числе',
                        marryChildren: true,
                        children: [
                            {
                                headerName: 'ЗПМ',
                                cellClass: rightAlignedCellClassName,
                                cellRenderer: AnalyticsCellRenderer,
                                cellRendererParams: {
                                    fieldName: 'zpm',
                                    fractionDigits: 0,
                                },
                            },
                        ],
                    },
                    {
                        headerName: 'МП',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'mp',
                            fractionDigits: 0,
                        },
                    },
                    {
                        headerName: 'НР',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'nr',
                            fractionDigits: 0,
                        },
                    },
                    {
                        headerName: 'СП',
                        cellClass: rightAlignedCellClassName,
                        cellRenderer: AnalyticsCellRenderer,
                        cellRendererParams: {
                            fieldName: 'sp',
                            fractionDigits: 0,
                        },
                    },
                ],
            },
            {
                headerName: 'ОП',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'op',
                    fractionDigits: 0,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Прочие затраты',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'others',
                    fractionDigits: 0,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Всего',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'total',
                    fractionDigits: 0,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Справочно МЗ',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'mz',
                    fractionDigits: 0,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Справочно ОЗ',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'oz',
                    fractionDigits: 0,
                },
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'ТЗР, чел.час',
                cellRenderer: AnalyticsCellRenderer,
                cellRendererParams: {
                    fieldName: 'tzr',
                    fractionDigits: 2,
                },
                cellClass: rightAlignedCellClassName,
            },
        ],
        [tableFilters],
    );
};
